import { Injectable, inject, signal, computed, DestroyRef } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { of } from 'rxjs';
import { switchMap, catchError, timeout, map } from 'rxjs/operators';
import { SystemApiService as GeneratedSystemService } from '@api/api/system.service';
import { LivenessResponse } from '@api/model/models';
import { LoggerService } from './logger.service';
import { BrowserService } from './browser.service';

/** Tri-state trading mode. `unknown` means we have no trustworthy reading yet. */
export type TradingMode = 'paper' | 'live' | 'unknown';

/**
 * RF-QA-172 · Outcome of one `/api/v1/health` poll, published so every
 * consumer (footer "API Online" pill, trading-mode badge) reads the SAME
 * request instead of running its own poller.
 */
export type HealthPollResult =
  | { readonly kind: 'ok'; readonly response: LivenessResponse; readonly at: Date }
  | { readonly kind: 'error'; readonly error: unknown; readonly at: Date };

/**
 * Centralized trading mode service.
 *
 * Provides a single source of truth for whether the system is in
 * paper trading mode or live trading mode. All components that need
 * to display or react to the trading mode should consume this service
 * instead of maintaining their own state.
 *
 * Data source: Polls the SIMPLE /api/v1/health endpoint (detailed=false) every
 * 30s, reading the authoritative SYSTEM posture from `paperTrading`.
 *
 * B7 — read the SIMPLE response, NOT `?detailed=true`:
 *   The simple `/health` branch hand-builds its JSON with an explicit
 *   `"paperTrading"` key (camelCase), sourced from the boot-time
 *   `ApiServerState.paper_trading` — the single authoritative system posture.
 *   The `?detailed=true` branch instead serializes the Rust `HealthResponse`
 *   struct, which has NO `rename_all = "camelCase"`, so it emits snake_case
 *   `paper_trading`. The generated TS `HealthResponse` type claims `paperTrading`
 *   either way, so a `detailed=true` poll would silently read `undefined` →
 *   `undefined ? 'paper' : 'live'` → ALWAYS LIVE, badging a paper deployment as
 *   live. We therefore call `healthCheck(false)`. Per-strategy / per-user paper
 *   config (`GET /risk/paper/mode`) is a SEPARATE concern and must NOT be read
 *   here — it is not the system posture.
 *
 * Fail-CLOSED, not fail-open (GAP-005):
 *   Earlier this service assumed PAPER on any health error. That was unsafe:
 *   the order ticket derives `isLive = !isPaperTrading()` and gates the
 *   large-live-order type-confirm on `isLive()`. A transient health outage
 *   while the backend was LIVE flipped the flag to PAPER, silently skipping
 *   the confirmation gate and badging the system PAPER for up to 30s.
 *
 *   The mode is now tri-state. We never *invent* PAPER:
 *     - On success  → set the real mode, clear staleness.
 *     - On error    → keep the last-known mode but flag it stale; if we have
 *                     never had a successful reading, the mode stays `unknown`.
 *   `isPaperTrading()` is true ONLY when the mode is a confirmed `paper`, so
 *   an `unknown` (or any non-paper) state makes `isLive()` true downstream and
 *   the confirmation gate fails closed (requires confirmation).
 *
 * Lifetime: This is a root-scoped service — the polling subscription
 * is intentionally never unsubscribed since it must live for the app's
 * entire lifetime.
 *
 * RF-QA-171 / RF-QA-172 · This is the ONLY `/api/v1/health` poller in the
 * app. The footer used to run a second `timer(1000, 30000)` against the same
 * endpoint (two requests ~1.9 s apart every 30 s); it now reads {@link health}.
 * The clock is {@link BrowserService.visibleTimer}, so polling pauses while
 * the tab is hidden and refreshes immediately when it becomes visible.
 */
@Injectable({ providedIn: 'root' })
export class TradingModeService {
  private readonly destroyRef = inject(DestroyRef);
  private readonly systemService = inject(GeneratedSystemService);
  private readonly logger = inject(LoggerService);
  private readonly browser = inject(BrowserService);

  private static readonly POLL_INTERVAL = 30_000;
  private static readonly POLL_TIMEOUT = 10_000;

  /** RF-QA-172 · Last outcome of the shared health poll (null until the first settles). */
  private readonly _health = signal<HealthPollResult | null>(null);

  /**
   * RF-QA-172 · The shared `/api/v1/health` poll result. Any number of
   * consumers can read this for the cost of ONE request per interval.
   */
  readonly health = this._health.asReadonly();

  /**
   * Current trading mode. Starts `unknown` (no reading yet) and is only ever
   * set to `paper`/`live` from a successful health response — never inferred.
   */
  private readonly _mode = signal<TradingMode>('unknown');

  /**
   * True when the most recent poll failed and we are showing a retained
   * last-known mode (or `unknown` if we never had one).
   */
  private readonly _isStale = signal<boolean>(false);

  /** Timestamp of the last successful check */
  private readonly _lastChecked = signal<Date | null>(null);

  /** Raw tri-state mode for consumers that need to distinguish `unknown`. */
  readonly mode = this._mode.asReadonly();

  /**
   * True ONLY when the mode is a confirmed `paper`. An `unknown` (or stale)
   * mode deliberately reads as NOT paper so safety gates fail closed.
   */
  readonly isPaperTrading = computed(() => this._mode() === 'paper');

  /** True once we have at least one trustworthy reading. */
  readonly isModeKnown = computed(() => this._mode() !== 'unknown');

  /** True when the displayed mode is unverified (last poll failed). */
  readonly isStale = this._isStale.asReadonly();

  /** Timestamp of the last successful health reading (null until first success). */
  readonly lastChecked = this._lastChecked.asReadonly();

  /** Short status text. `UNKNOWN` is surfaced explicitly rather than guessed. */
  readonly tradingModeText = computed(() => {
    switch (this._mode()) {
      case 'paper':
        return 'PAPER';
      case 'live':
        return 'LIVE';
      default:
        return 'UNKNOWN';
    }
  });

  /** Long status label, mirroring {@link tradingModeText}. */
  readonly tradingModeLabel = computed(() => {
    switch (this._mode()) {
      case 'paper':
        return 'PAPER TRADING';
      case 'live':
        return 'LIVE TRADING';
      default:
        return 'MODE UNKNOWN';
    }
  });

  constructor() {
    this.startPolling();
  }

  private startPolling(): void {
    // Root service — subscription lives for the app's entire lifetime.
    // takeUntilDestroyed added for pattern consistency with GAP-FE-030.
    // RF-QA-171: visibleTimer replaces interval()+startWith(0) — same 30 s
    // cadence and immediate first poll, but paused while the tab is hidden.
    this.browser.visibleTimer(TradingModeService.POLL_INTERVAL)
      .pipe(
        switchMap(() => {
          // A retained posture is unverified while its refresh is in flight.
          // This also prevents a black-holed health request from leaving an
          // old PAPER/LIVE reading trusted indefinitely.
          this._isStale.set(true);
          return (
          // B7 — detailed=false: the SIMPLE /health response carries the
          // authoritative system posture as camelCase `paperTrading`. See the
          // class docstring for why `true` here would always read as LIVE.
          this.systemService.healthCheck(false).pipe(
            timeout({ first: TradingModeService.POLL_TIMEOUT }),
            map((response): HealthPollResult => ({ kind: 'ok', response, at: new Date() })),
            catchError((error: unknown) => {
              this.logger.warn('Failed to fetch trading mode status', { error });
              return of<HealthPollResult>({ kind: 'error', error, at: new Date() });
            })
          )
          );
        }),
        takeUntilDestroyed(this.destroyRef),
      )
      .subscribe((result: HealthPollResult) => {
        // RF-QA-172: publish the raw outcome for the other consumers first.
        this._health.set(result);

        if (result.kind === 'error') {
          // GAP-005: do NOT assume PAPER on error. Retain the last-known mode
          // (which may be `unknown`) and flag it stale so the UI can warn and
          // safety gates downstream keep failing closed for non-paper modes.
          this._isStale.set(true);
          return;
        }

        const response = result.response;
        if (typeof response.paperTrading !== 'boolean') {
          this._isStale.set(true);
          this.logger.warn('Trading mode health response was malformed', {
            paperTradingType: typeof response.paperTrading,
          });
          return;
        }

        this._mode.set(response.paperTrading ? 'paper' : 'live');
        this._isStale.set(false);
        this._lastChecked.set(
          response.timestamp ? new Date(response.timestamp) : new Date()
        );
      });
  }
}
